import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { datadogLogs } from "@datadog/browser-logs";
import { Button, Notification, NotifyType, SpinningLoader, SvgIcon } from "common/components";
import {
  AuthMe,
  AuthProviders,
  redirectToOkta,
  redirectToAzure,
  redirectToEgerdau,
} from "common/services";
import logo from "assets/logo_gerdau_blue_horizontal.svg?url";
import ggMark from "assets/logo_gerdau_gg.svg?url";
import styles from "./LoginPage.module.scss";

interface LoginPageProps {
  providers: AuthProviders;
  /** Current user from /auth/me when a QQT cookie session already exists. */
  me?: AuthMe;
  /** True when a backend cookie session is active. */
  isAuthenticated: boolean;
  /**
   * When set, replaces the form with an inline spinner (inside the card) showing this message —
   * e.g. while `/auth/me` resolves an inherited session, or the profile/SAP data hydrates.
   */
  loadingMessage?: string | null;
  /** True when `GET /auth/providers` failed. Shows a retry card instead of the login form. */
  providersError?: boolean;
  /** True while a provider-fetch retry (triggered from the error card) is in flight. */
  providersRetrying?: boolean;
  /** Re-attempts `GET /auth/providers`. Required when `providersError` is true. */
  onRetryProviders?: () => void;
}

const SILENT_FAILED_PARAM = "silent_failed";
// Set by the backend OIDC callback (AuthController) when the user authenticated successfully at
// Okta/Entra but has no QQT entitlement (no MuleSoft/ORO profile). No session cookie was ever
// issued for this login — the rejection happens before any session exists, unlike the old
// behavior of only discovering this later via a 404 from /users/current.
const NOT_AUTHORIZED_PARAM = "not_authorized";
// Correlation id minted by the backend callback (AuthController) for a rejected login. Logged
// here and shown to the user so a support ticket can be matched to the corresponding backend
// Datadog log line for the same login attempt.
const TRACE_ID_PARAM = "trace_id";
// One-shot marker (per browser tab) so we attempt a silent SSO at most once on launch and
// never loop between QQT and Okta.
const SILENT_ATTEMPT_KEY = "qqt_okta_silent_attempted";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Gerdau employees live in Microsoft Entra ID and do not authenticate through Okta, so a
// @gerdau.com address is routed straight to the direct Entra login path (ADR 0002). Hardcoded
// for now; externalize to a config-driven domain map if more Gerdau-owned domains are added.
const GERDAU_DOMAIN = "gerdau.com";
const isGerdauEmail = (value: string): boolean =>
  value.trim().toLowerCase().endsWith(`@${GERDAU_DOMAIN}`);

const readQuery = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get("source") || undefined,
    idp: params.get("idp") || undefined,
    return: params.get("return") || undefined,
    silentFailed: params.get(SILENT_FAILED_PARAM) === "1",
    notAuthorized: params.get(NOT_AUTHORIZED_PARAM) === "1",
    traceId: params.get(TRACE_ID_PARAM) || undefined,
  };
};

const silentAlreadyAttempted = () => {
  try {
    return window.sessionStorage.getItem(SILENT_ATTEMPT_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Suppresses the one-shot silent SSO on the next login-page load. Exported so sign-out can call
 * it: the user then lands on the login form instead of being immediately re-authenticated via
 * the shared Okta session.
 */
export const markSilentAttempted = () => {
  try {
    window.sessionStorage.setItem(SILENT_ATTEMPT_KEY, "1");
  } catch {
    /* sessionStorage unavailable — fall through to the login form */
  }
};

/**
 * Login surface shown when the user is not yet authenticated.
 *
 * <p>On launch it makes a single silent SSO attempt (`prompt=none`) so a session inherited
 * from another Gerdau app is picked up transparently. When there is <b>no Okta session</b>
 * (silent SSO fails) and Okta is enabled, it renders the branded email SSO home page: the user
 * enters their e-mail and we start the Okta Authorization Code flow with that address as
 * `login_hint`, so Okta prompts them for credentials / MFA.</p>
 */
export const LoginPage: React.FC<LoginPageProps> = ({
  providers,
  me,
  isAuthenticated,
  loadingMessage,
  providersError = false,
  providersRetrying = false,
  onRetryProviders,
}) => {
  const query = readQuery();
  const { source, idp, return: returnUrl } = query;
  // Capture once on mount so the notice survives after we scrub the param from the address bar.
  const [silentFailed] = useState(query.silentFailed);
  const [notAuthorized] = useState(query.notAuthorized);
  const [traceId] = useState(query.traceId);

  // Remove ?silent_failed=1 / ?not_authorized=1 / ?trace_id=... from the URL without a reload —
  // the flags live in state, and a clean address bar avoids exposing internal auth plumbing to
  // the user.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      !params.has(SILENT_FAILED_PARAM) &&
      !params.has(NOT_AUTHORIZED_PARAM) &&
      !params.has(TRACE_ID_PARAM)
    ) {
      return;
    }
    params.delete(SILENT_FAILED_PARAM);
    params.delete(NOT_AUTHORIZED_PARAM);
    params.delete(TRACE_ID_PARAM);
    const qs = params.toString();
    const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, []);

  // Log the rejection to Datadog (matching the backend's warning log for the same traceId) so
  // the incident is visible client-side too — e.g. if the user never files a support ticket.
  useEffect(() => {
    if (!notAuthorized) return;
    datadogLogs.logger.warn("[Login] Rejected: account has no QQT entitlement", {
      name: "Login rejected: not authorized",
      traceId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A URL-provided idp wins; otherwise fall back to the backend-configured default provider.
  // Blank / "okta" means Okta-local; "azure" routes through Microsoft Entra ID.
  const effectiveIdp = idp || providers.defaultIdp || undefined;
  const wantsEgerdau = effectiveIdp === "egerdau";
  const usingMicrosoft = effectiveIdp === "azure";

  const [email, setEmail] = useState("");
  const emailValid = EMAIL_PATTERN.test(email.trim());
  const emailIsGerdau = isGerdauEmail(email);
  // A @gerdau.com address routes to Entra when the backend has the Azure provider enabled.
  const routesToAzure = providers.azure && emailIsGerdau;
  // @gerdau.com accounts authenticate ONLY through Microsoft Entra ID — they do not work with
  // Okta. So when Entra is disabled we reject the login outright rather than falling back to
  // Okta (which would fail for these users anyway).
  const gerdauEntraDisabled = emailIsGerdau && !providers.azure;

  useEffect(() => {
    if (providersError) return; // no confirmed providers — nothing to redirect to
    if (isAuthenticated) return; // already signed in — nothing to start
    if (notAuthorized) {
      // The user has a valid upstream Okta/Entra session but no QQT entitlement. Retrying a
      // silent SSO would just succeed again and bounce them straight back here — mark it
      // attempted so they land on the form instead of looping.
      markSilentAttempted();
      return;
    }
    if (silentFailed) {
      markSilentAttempted(); // came back from a failed silent SSO — show the form, don't retry
      return;
    }
    if (silentAlreadyAttempted()) return;
    if (wantsEgerdau) return; // explicit eGerdau deep-links are user-initiated, not silent
    if (providers.okta) {
      markSilentAttempted();
      redirectToOkta({ idp: effectiveIdp, source, return: returnUrl, silent: true });
    }
  }, [
    providersError,
    isAuthenticated,
    notAuthorized,
    silentFailed,
    effectiveIdp,
    wantsEgerdau,
    source,
    returnUrl,
    providers,
  ]);

  // Shared page chrome (header/footer) around whichever card body applies for the current state.
  const renderShell = (body: React.ReactNode) => (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <img src={ggMark} alt="Gerdau" className={styles.topBarLogo} />
      </header>
      <main className={styles.main}>
        {body}
        <div className={styles.footerBrand}>
          <img src={logo} alt="Gerdau" className={styles.footerLogo} />
          <span className={styles.footerTagline}>Shape the future</span>
        </div>
        <div className={styles.footer}>
          <a href="https://www.gerdau.com" className={styles.footerLink}>
            Gerdau Site
          </a>
          <span className={styles.footerDivider}>|</span>
          <a href="https://www.gerdau.com/privacy" className={styles.footerLink}>
            Privacy Policy
          </a>
          <span className={styles.footerDivider}>|</span>
          <a href="https://www.gerdau.com/terms" className={styles.footerLink}>
            Terms &amp; Conditions
          </a>
          <div className={styles.copyright}>
            COPYRIGHT © 2014 - {new Date().getFullYear()} GERDAU | ALL RIGHTS RESERVED.
          </div>
        </div>
      </main>
      <span className={styles.locationCaption}>Cambridge, ON</span>
    </div>
  );

  // GET /auth/providers failed — we don't know which providers are available, so there is
  // nothing safe to redirect to (Okta, Azure, and eGerdau could all be misrepresented). Show a
  // retry card inside the login shell instead of a blank page or a guess.
  if (providersError) {
    return renderShell(
      <div className={styles.card}>
        <h1 className={styles.title}>Quick Quote Tool</h1>
        <div className={styles.notice}>
          <Notification
            type={NotifyType.Error}
            text="We couldn't reach the sign-in service. Please check your connection and try again."
          />
        </div>
        <Button
          type="button"
          className={styles.next}
          disabled={providersRetrying || !onRetryProviders}
          onClick={() => onRetryProviders?.()}
        >
          {providersRetrying ? "Retrying…" : "Try again"}
        </Button>
        <div className={styles.contact}>
          <div className={styles.contactTitle}>Any Questions? Contact us.</div>
          <div>1-800-237-0230</div>
          <a href="mailto:eGerdau_support@gerdau.com" className={styles.contactMail}>
            <SvgIcon name="email" attributes={{ width: "14px", height: "14px" }} className="me-4" />
            eGerdau_support@gerdau.com
          </a>
        </div>
        <div className={styles.cardBrand}>
          <img src={logo} alt="Gerdau" className={styles.cardLogo} />
          <span className={styles.tagline}>Shape the future</span>
        </div>
      </div>,
    );
  }

  // A silent SSO attempt is in flight on launch, or the caller is still hydrating the session /
  // profile / SAP data (e.g. after sign-in) — keep the branded card up with an inline spinner
  // instead of either a full-page overlay or a flash of the login form. Checked before the
  // "signed in" card below so an authenticated-but-still-hydrating session shows the spinner,
  // not a premature confirmation.
  const silentSsoInFlight =
    !silentFailed && !notAuthorized && !silentAlreadyAttempted() && providers.okta && !wantsEgerdau;
  if (loadingMessage || (!isAuthenticated && silentSsoInFlight)) {
    return renderShell(
      <div className={styles.card}>
        <h1 className={styles.title}>Quick Quote Tool</h1>
        <SpinningLoader inline show message={loadingMessage ?? "Signing you in"} />
      </div>,
    );
  }

  // Authenticated via a (possibly inherited) Okta session — show the current user data.
  if (isAuthenticated && me) {
    return renderShell(
      <div className={styles.card}>
        <h1 className={styles.title}>Signed in</h1>
        <div className={styles.sessionCard}>
          <div>
            <strong>{me.displayName || me.email}</strong>
          </div>
          {me.email && <div>{me.email}</div>}
          <div className={styles.muted}>Authenticated via {me.provider}</div>
          {me.groups && me.groups.length > 0 && (
            <div className={styles.muted}>Groups: {me.groups.join(", ")}</div>
          )}
        </div>
        <div className={styles.cardBrand}>
          <img src={logo} alt="Gerdau" className={styles.cardLogo} />
          <span className={styles.tagline}>Shape the future</span>
        </div>
      </div>,
    );
  }

  const startLogin = () => {
    if (!emailValid || gerdauEntraDisabled) return;
    const trimmedEmail = email.trim();
    // @gerdau.com -> direct Microsoft Entra ID. The login_hint lets Entra complete silently for
    // users already signed into Microsoft, and only prompt when needed.
    if (routesToAzure) {
      redirectToAzure({ source, return: returnUrl, login_hint: trimmedEmail });
      return;
    }
    redirectToOkta({
      idp: effectiveIdp,
      source,
      return: returnUrl,
      login_hint: trimmedEmail,
    });
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    startLogin();
  };

  return renderShell(
    <form className={styles.card} onSubmit={onSubmit}>
      <h1 className={styles.title}>Quick Quote Tool</h1>
      <p className={styles.subtitle}>
        Please enter login using your e-mail below to continue to the{" "}
        <strong>Gerdau Quick Quote Tool</strong>.
      </p>

      {notAuthorized && (
        <div className={styles.notice}>
          <Notification
            type={NotifyType.Error}
            text="Your account does not have access to Quick Quote Tool. Please contact support if you believe this is an error."
          />
          {traceId && (
            <div className={styles.muted}>
              Reference ID: <span data-testid="login-trace-id">{traceId}</span>
            </div>
          )}
        </div>
      )}

      {!notAuthorized && silentFailed && (
        <div className={styles.notice}>
          <Notification
            type={NotifyType.Error}
            text="We couldn't sign you in automatically. Please enter your e-mail to continue."
          />
        </div>
      )}

      <label className={styles.label} htmlFor="qqt-login-email">
        Email
      </label>
      <div className={styles.inputWrap}>
        <input
          id="qqt-login-email"
          type="email"
          autoFocus
          autoComplete="email"
          className={clsx("form-control font-size-14 text-dark", styles.input)}
          placeholder="name@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {email && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear email"
            onClick={() => setEmail("")}
          >
            <SvgIcon name="clear" attributes={{ width: "16px", height: "16px" }} />
          </button>
        )}
      </div>

      {gerdauEntraDisabled && (
        <div className={styles.notice}>
          <Notification
            type={NotifyType.Error}
            text="Microsoft sign-in for @gerdau.com accounts is currently unavailable. Please contact support if you need access."
          />
        </div>
      )}

      <Button type="submit" className={styles.next} disabled={!emailValid || gerdauEntraDisabled}>
        {usingMicrosoft || routesToAzure ? "Next — Continue with Microsoft" : "Next"}
      </Button>

      {providers.egerdau && (
        <button type="button" className={styles.altLink} onClick={() => redirectToEgerdau()}>
          Continue with eGerdau instead
        </button>
      )}

      <div className={styles.contact}>
        <div className={styles.contactTitle}>Any Questions? Contact us.</div>
        <div>1-800-237-0230</div>
        <a href="mailto:eGerdau_support@gerdau.com" className={styles.contactMail}>
          <SvgIcon name="email" attributes={{ width: "14px", height: "14px" }} className="me-4" />
          eGerdau_support@gerdau.com
        </a>
      </div>

      <div className={styles.cardBrand}>
        <img src={logo} alt="Gerdau" className={styles.cardLogo} />
        <span className={styles.tagline}>Shape the future</span>
      </div>
    </form>,
  );
};
